import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, ShoppingBag, MessageSquare, Star, Package,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProductCard } from "../components/ProductCard";
import { RatingSummary, RatingPanel } from "../components/Rating";
import { FollowButton } from "../components/FollowButton";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export default function SellerProfilePage() {
  const { name } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get(`/sellers/${encodeURIComponent(name)}`);
      setProfile(res.data);
    } catch {
      toast.error("Seller not found");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [name]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!profile) return null;

  const initial = profile.name.charAt(0).toUpperCase();
  const joinedDate = profile.joined ? new Date(profile.joined).toLocaleDateString(undefined, {
    year: "numeric", month: "short",
  }) : "—";

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Hero */}
      <div className="rounded-md border border-border bg-white p-6 md:p-10 mb-10">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="h-24 w-24 md:h-28 md:w-28 rounded-full bg-foreground text-background flex items-center justify-center font-display text-4xl md:text-5xl font-bold flex-shrink-0">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">
                  {t("seller_profile")}
                </div>
                <h1
                  data-testid="seller-name"
                  className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight break-words"
                >
                  {profile.name}
                </h1>
              </div>
              <FollowButton sellerName={profile.name} />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-6">
              {profile.rating_count > 0 && (
                <RatingSummary avg={profile.rating_avg} count={profile.rating_count} size={5} />
              )}
              <div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  {t("total_products")}
                </div>
                <div className="font-display text-xl font-bold tabular-nums">
                  {profile.product_count}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  {t("total_offers")}
                </div>
                <div className="font-display text-xl font-bold tabular-nums">
                  {profile.offer_count}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  {t("joined")}
                </div>
                <div className="font-display text-xl font-bold">{joinedDate}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="catalog" className="w-full">
        <TabsList className="bg-white border border-border rounded-md p-1 h-auto w-full sm:w-auto flex-nowrap overflow-x-auto scrollbar-none justify-start">
          <TabsTrigger data-testid="tab-catalog" value="catalog" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <ShoppingBag className="h-3.5 w-3.5" />
            {t("catalog")}
            <span className="font-mono-brand text-[10px] opacity-70">{profile.product_count}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-quotes" value="quotes" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <MessageSquare className="h-3.5 w-3.5" />
            {t("quotes_given")}
            <span className="font-mono-brand text-[10px] opacity-70">{profile.offer_count}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-ratings" value="ratings" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Star className="h-3.5 w-3.5" />
            {t("rate_seller")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="mt-8">
          {profile.products.length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
              <ShoppingBag className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">{t("no_products_seller")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {profile.products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="quotes" className="mt-8">
          {profile.offers.length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
              <MessageSquare className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">{t("no_offers_seller")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {profile.offers.map((o, i) => (
                <div
                  key={o.id}
                  data-testid={`seller-offer-${o.id}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  className="fade-up rounded-md border border-border bg-white p-5 md:p-6 hover:border-foreground/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/r/${o.request_id}`)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">
                        Offer on request
                      </div>
                      <div className="text-sm text-foreground/80 line-clamp-2">
                        {o.description}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">
                        Price
                      </div>
                      <div className="font-mono-brand text-lg font-bold text-primary">{o.price}</div>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold">
                    <span className="h-1.5 w-1.5 rounded-full bg-accent"></span>
                    {o.availability}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ratings" className="mt-8">
          <div className="max-w-2xl">
            <div className="mb-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">
                {t("rate_seller")}
              </div>
              <RatingSummary avg={profile.rating_avg} count={profile.rating_count} size={5} />
            </div>
            <RatingPanel targetType="seller" targetId={profile.name} onRated={load} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
